"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"

interface Session {
  id: string
  name: string
  drawTime: string
  active: boolean
  date: string
}

export function CreateResults() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [selectedSession, setSelectedSession] = useState("")
  const [results, setResults] = useState([
    { category: "1st", number: "" },
    { category: "2nd", number: "" },
    { category: "3rd", number: "" },
    { category: "4th", number: "" },
    { category: "5th", number: "" }
  ])
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE_URL}/sessions/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    }
  }

  const handleResultChange = (index: number, field: string, value: string) => {
    const newResults = [...results]
    newResults[index] = { ...newResults[index], [field]: value }
    setResults(newResults)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedSession) {
      toast({
        title: "Error",
        description: "Please select a session",
        variant: "destructive",
      })
      return
    }

    // Validate all results have numbers
    const invalidResults = results.filter(r => !r.number || r.number === "")
    if (invalidResults.length > 0) {
      toast({
        title: "Error",
        description: "Please enter numbers for all categories",
        variant: "destructive",
      })
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE_URL}/admin/create-results`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: selectedSession,
          results: results.map(r => ({
            category: r.category,
            number: parseInt(r.number)
          }))
        }),
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: "Results created and winnings calculated successfully!",
          variant: "default",
        })
        setResults(results.map(r => ({ ...r, number: "" })))
        setSelectedSession("")
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to create results",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error creating results:', error)
      toast({
        title: "Error",
        description: "Failed to create results",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Lottery Results</CardTitle>
        <CardDescription>
          Enter winning numbers for each category and calculate winnings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="session">Select Session</Label>
            <Select value={selectedSession} onValueChange={setSelectedSession}>
              <SelectTrigger>
                <SelectValue placeholder="Select session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map(session => (
                  <SelectItem key={session.id} value={session.id}>
                    {session.name} - {session.drawTime}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={fetchSessions}
            >
              Load Sessions
            </Button>
          </div>

          <div className="space-y-3">
            <Label>Winning Numbers</Label>
            {results.map((result, index) => (
              <div key={result.category} className="flex items-center gap-3">
                <div className="w-20 font-medium">{result.category}</div>
                <Input
                  type="number"
                  min="0"
                  max="9"
                  value={result.number}
                  onChange={(e) => handleResultChange(index, 'number', e.target.value)}
                  placeholder="0-9"
                  className="w-20"
                  required
                />
              </div>
            ))}
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Prize Multipliers:</strong> 1st (90x), 2nd (60x), 3rd (40x), 4th (20x), 5th (10x)
            </p>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={loading || !selectedSession}
          >
            {loading ? "Calculating Winnings..." : "Create Results & Calculate Winnings"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}